import React from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import {
  BloombergLogo,
  BloombergLogoUK,
  BloombergLogoWeekend,
} from '@bloomberg/media-ui-icons';

import { useNavContext } from '../Nav/NavContext';
import { REGIONS, THEMES } from '../types';

import styles from './Logo.module.css';

export type LogoProps = GlobalProps & {
  isWeekend?: boolean;
  theme?: THEMES;
  name?: string;
  trackingFunction: (eventLabel: string) => void;
};

const WeekendLogo = ({
  trackingFunction,
}: {
  trackingFunction: (eventLabel: string) => void;
}) => {
  const href = '/';
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackingFunction('bloomberg');
    window.location.assign(href);
  };

  return (
    <a href={href} className={styles.logoWeekend} onClick={onClick}>
      <BloombergLogoWeekend />
    </a>
  );
};

const UKLogo = ({
  trackingFunction,
}: {
  trackingFunction: (eventLabel: string) => void;
}) => {
  const href = '/uk';
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackingFunction('bloomberg');
    window.location.assign(href);
  };

  return (
    <a href={href} className={styles.logoUK} onClick={onClick}>
      <BloombergLogoUK />
    </a>
  );
};

const DefaultLogo = ({
  trackingFunction,
}: {
  trackingFunction: (eventLabel: string) => void;
}) => {
  const { region } = useNavContext();
  const href = region === REGIONS.JAPAN ? 'https://www.bloomberg.co.jp' : '/';
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackingFunction('bloomberg');
    window.location.assign(href);
  };

  return (
    <a href={href} className={styles.logo} onClick={onClick}>
      <BloombergLogo />
    </a>
  );
};

const LogoLink = ({ isWeekend, trackingFunction }: LogoProps) => {
  const { region } = useNavContext();

  if (isWeekend) return <WeekendLogo trackingFunction={trackingFunction} />;

  return region === REGIONS.UK ? (
    <UKLogo trackingFunction={trackingFunction} />
  ) : (
    <DefaultLogo trackingFunction={trackingFunction} />
  );
};

export const Logo = React.forwardRef<HTMLDivElement, LogoProps>(
  function LogoComponent(props: LogoProps, ref) {
    const { isWeekend, theme, trackingFunction } = props;

    const componentName = useSlug('logo');

    const cx = classes([
      styles.logo,
      {
        [styles.light]: theme === THEMES.LIGHT,
        [styles.dark]: theme === THEMES.DARK,
        [styles.blue]: theme === THEMES.BLUE,
      },
    ]);

    return (
      <div
        className={cx}
        aria-label="logo"
        data-component={componentName}
        ref={ref}
      >
        <LogoLink trackingFunction={trackingFunction} isWeekend={isWeekend} />
      </div>
    );
  }
);
